'use client';

import { c as _c } from "react/compiler-runtime";
import { getImageProxy, isSdkError, useGenerateImage } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import type { ChangeEvent, Dispatch, FormEvent, RefObject, SetStateAction } from 'react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { Crop, PixelCrop } from 'react-image-crop';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { ImageUploadDropzone } from '@/components/images/image-upload-dropzone';
import { ImageShimmer } from '@/components/images/optimized-image';
import type { ImageSearchResult } from '@/components/images/search-grid';
import { SearchGrid } from '@/components/images/search-grid';
import { IMAGE_UPLOAD_MAX_MB, validateImageUpload } from '@/components/images/validation';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Alert, AlertDescription } from '@/components/ui/app-alert';
import { AppTabs, type AppTabsItem } from '@/components/ui/app-tabs';
import { Icon } from '@/components/ui/icon';
import { RepeatIcon, Search01Icon, SparklesIcon, Upload01Icon } from '@/components/ui/icon/glyphs';
import { InputField } from '@/components/ui/input-field';
import { TOAST_IDS } from '@/config/toast-ids';
import { onEvent } from '@/events';
import type { ImageGenerationFailureReason, ImageGenerationMode, ImageSearchFailureReason } from '@/events/image-generation';
import { checkGate } from '@/lib/analytics';
import { getCroppedDraftImage } from '@/lib/images/get-cropped-blob';
import { buildOutcomeSearchQuery } from '@/lib/launchpad/outcome-search-query';
import { logger } from '@/lib/telemetry/logger';
import { showToast } from '@/lib/ui/toast';
import { cn } from '@/lib/ui/utils';
export interface MarketImageDraft {
  readonly croppedFile: File;
  readonly previewUrl: string;
  /** Bounded WebP data-URI of the cropped bytes, for durable localStorage
   *  draft persistence + rehydrate reconstruction (MOR-847). `undefined` when
   *  the environment couldn't encode it; the in-session pick is unaffected. */
  readonly persistedBytes?: string | undefined;
}
export interface MarketImagePickerProps {
  mode: ImageGenerationMode;
  marketTitle: string;
  /** Required when mode === 'outcome'. Ignored for 'cover'. */
  outcomeLabel?: string | undefined;
  /**
   * Deferred market-image lifecycle: crop into a local File draft and let
   * the parent keep it until create. The create hook bulk-uploads every draft
   * through the generated SDK once it has the final compact image order.
   */
  onPending: (draft: MarketImageDraft) => void;
  onReject: (error: Error) => void;
  /** Optional — modal wrappers pass this so the Cancel button can close the dialog. Cover step omits it. */
  onCancel?: () => void;
  /** When true, the Generate-tab idle/error CTA row renders a Cancel button next to the primary CTA. Default: false. */
  showCancel?: boolean;
  /**
   * Optional ref the picker uses to publish an internal "back" handler when it
   * has somewhere to go inside itself (e.g. from the search-tab crop view
   * back to the results grid). The parent wizard reads this ref from its own
   * Back button so a single button traverses both layers without an extra
   * inline back button inside the picker.
   */
  backInterceptorRef?: RefObject<(() => void) | null> | undefined;
  /**
   * Optional callback the picker uses to publish an internal "confirm" handler
   * when it is in a state where the user can finalise the image (cropping a
   * generated/searched image). The parent wizard subscribes to drive its own
   * primary action. When this prop is set, the picker hides its inline Confirm
   * button. The picker calls back with `null` when no confirm is available.
   */
  onConfirmStateChange?: ((state: {
    confirm: () => Promise<void>;
    canConfirm: boolean;
    confirming: boolean;
  } | null) => void) | undefined;
  /**
   * When true (default), the picker renders its own 2-step indicator at the
   * top. The standalone outcome modal keeps this on. The proposal wizard
   * cover step disables it because the wizard already shows its 5-step
   * indicator.
   */
  showStepIndicator?: boolean;
}
type GenerateState = {
  kind: 'idle';
} | {
  kind: 'generating';
} | {
  kind: 'result';
  imageUrl: string;
} | {
  kind: 'error';
  reason: ImageGenerationFailureReason;
};
type SearchState = {
  kind: 'idle';
} | {
  kind: 'results';
} | {
  kind: 'cropping';
  selected: ImageSearchResult;
} | {
  kind: 'error';
  reason: ImageSearchFailureReason;
};
type UploadState = {
  kind: 'idle';
} | {
  kind: 'cropping';
  src: string;
  file: File;
};
type TabValue = 'search' | 'generate' | 'upload';
const INITIAL_CROP_DEFAULT: Crop = {
  unit: '%',
  x: 10,
  y: 10,
  width: 80,
  height: 80
};

// Wall-clock read isolated to a module function. The React Compiler flags a
// direct `Date.now()` inside a memoizable component callback (`runGeneration`)
// as an impure render-time call — even though it only ever runs from a click.
// Reading the clock here keeps that callback Compiler-eligible.
function nowMs(): number {
  return Date.now();
}

// failure body is a raw string; shape-checked before any field access.
// ALLOW-UNKNOWN: mutation error body is opaque (open SDK error shape).
function extractFailureReason(err: unknown): string | undefined {
  // ALLOW-UNKNOWN: speculative `.body` field on a thrown mutation error — open shape.
  const body = (err as {
    body?: unknown;
  }).body;
  if (typeof body !== 'string' || body.length === 0) return undefined;
  // ALLOW-UNKNOWN: JSON.parse result is structurally unknown until shape-checked.
  let parsed: unknown;
  try {
    parsed = JSON.parse(body);
  } catch {
    // SILENT-CATCH: non-JSON error body falls through to the generic toast message.
    return undefined;
  }
  if (parsed === null || typeof parsed !== 'object') return undefined;
  // ALLOW-UNKNOWN: speculative `.reason` field on the parsed JSON — open shape.
  const reason = (parsed as {
    reason?: unknown;
  }).reason;
  return typeof reason === 'string' ? reason : undefined;
}

// ALLOW-UNKNOWN: error from a TanStack mutation can be any thrown value — narrowed below via the v2 SdkError type guard.
function classifyError(err: unknown): ImageGenerationFailureReason {
  if (err instanceof DOMException && err.name === 'AbortError') return 'unknown';
  if (isSdkError(err)) {
    // The backend image routes return a non-canonical failure body
    // `{ reason: 'nsfw' | 'model' | 'invalid' | 'unconfigured' |
    // 'aborted' | 'quota_exceeded', detail? }`. The raw body is attached
    // to the SdkError as `body` by throwHttpError; parse it here so
    // moderation rejects keep their dedicated user-facing reason
    // instead of falling through to `'unknown'`.
    const reason = extractFailureReason(err);
    if (reason === 'nsfw') return 'nsfw';
    if (err.category === 'rate-limited') return 'rate_limit';
    if (err.category === 'server') return 'model';
    if (err.category === 'network') return 'network';
    return 'unknown';
  }
  if (err instanceof TypeError) return 'network';
  return 'unknown';
}

/**
 * All of the market-image picker's state, refs, handlers, and published
 * effects (back-interceptor + confirm-state + auto-search). Kept as a hook in
 * this file (not a separate module) so the component's intentional
 * controlled-dialog patterns keep their file-keyed verified-false-positive
 * exemptions (prefer-useReducer, the searchInput/searchQuery decouple, the
 * one-shot auto-search effect, and the imperative parent-callback effects).
 */
function useMarketImagePicker(props) {
  const $ = _c(131);
  const {
    mode,
    marketTitle,
    outcomeLabel,
    onPending,
    onReject,
    onCancel,
    showCancel: t0,
    backInterceptorRef,
    onConfirmStateChange
  } = props;
  const showCancel = t0 === undefined ? false : t0;
  const t = useTranslations("launchpad.imageGen");
  const tImageUpload = useTranslations("image_upload");
  const [activeTab, setActiveTab] = useState("search");
  const [userPrompt, setUserPrompt] = useState("");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      kind: "idle"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [generateState, setGenerateState] = useState(t1);
  const abortControllerRef = useRef(null);
  const generationStartRef = useRef(0);
  const [cropRegion, setCropRegion] = useState(INITIAL_CROP_DEFAULT);
  const [completedCrop, setCompletedCrop] = useState(undefined);
  const [isConfirming, setIsConfirming] = useState(false);
  const cropImgRef = useRef(null);
  let t2;
  if ($[1] !== marketTitle || $[2] !== mode || $[3] !== outcomeLabel) {
    t2 = mode === "outcome" && outcomeLabel !== undefined && outcomeLabel.length > 0 ? buildOutcomeSearchQuery(marketTitle, outcomeLabel) : marketTitle;
    $[1] = marketTitle;
    $[2] = mode;
    $[3] = outcomeLabel;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const initialSearchInput = t2;
  const [searchInput, setSearchInput] = useState(initialSearchInput);
  const [searchQuery, setSearchQuery] = useState("");
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      kind: "idle"
    };
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const [searchState, setSearchState] = useState(t3);
  const hasAutoFiredRef = useRef(false);
  const searchStartTimeRef = useRef(0);
  const lastSeenPageCountRef = useRef(0);
  const [searchCropRegion, setSearchCropRegion] = useState(INITIAL_CROP_DEFAULT);
  const [searchCompletedCrop, setSearchCompletedCrop] = useState(undefined);
  const [isSearchConfirming, setIsSearchConfirming] = useState(false);
  const searchCropImgRef = useRef(null);
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      kind: "idle"
    };
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const [uploadState, setUploadState] = useState(t4);
  const [uploadCropRegion, setUploadCropRegion] = useState(INITIAL_CROP_DEFAULT);
  const [uploadCompletedCrop, setUploadCompletedCrop] = useState(undefined);
  const [isUploadConfirming, setIsUploadConfirming] = useState(false);
  const uploadCropImgRef = useRef(null);
  let t5;
  if ($[7] !== userPrompt) {
    t5 = userPrompt.trim();
    $[7] = userPrompt;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const trimmedPrompt = t5;
  const hasUserPrompt = trimmedPrompt.length > 0;
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = function abortInFlight() {
      if (abortControllerRef.current !== null) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const abortInFlight = t6;
  let t7;
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => () => {
      if (abortControllerRef.current !== null) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
    t8 = [];
    $[10] = t7;
    $[11] = t8;
  } else {
    t7 = $[10];
    t8 = $[11];
  }
  useEffect(t7, t8);
  const generateMutation = useGenerateImage();
  let t9;
  if ($[12] !== generateMutation || $[13] !== hasUserPrompt || $[14] !== marketTitle || $[15] !== mode || $[16] !== outcomeLabel || $[17] !== t || $[18] !== trimmedPrompt) {
    const runGeneration = retried => {
      abortInFlight();
      const controller = new AbortController();
      abortControllerRef.current = controller;
      generationStartRef.current = nowMs();
      setGenerateState({
        kind: "generating"
      });
      onEvent("image_generation_started", {
        mode,
        hasUserPrompt
      });
      if (retried) {
        onEvent("image_generation_retried", {
          mode
        });
      }
      if (mode !== "outcome" && mode !== "cover") {
        logger.error("market-picker received non-launchpad mode", {
          mode
        });
        setGenerateState({
          kind: "error",
          reason: "unknown"
        });
        return;
      }
      generateMutation.mutate({
        mode,
        marketTitle,
        ...(outcomeLabel !== undefined ? {
          outcomeLabel
        } : {}),
        ...(hasUserPrompt ? {
          userPrompt: trimmedPrompt
        } : {})
      }, {
        onSuccess: raw => {
          const data = raw as {
            imageUrl: string;
            promptUsed: string;
          };
          if (abortControllerRef.current !== controller) {
            return;
          }
          abortControllerRef.current = null;
          setCropRegion(INITIAL_CROP_DEFAULT);
          setCompletedCrop(undefined);
          setGenerateState({
            kind: "result",
            imageUrl: data.imageUrl
          });
          const latencyMs = nowMs() - generationStartRef.current;
          onEvent("image_generation_completed", {
            mode,
            latencyMs,
            hasUserPrompt
          });
        },
        onError: err => {
          if (err instanceof DOMException && err.name === "AbortError") {
            if (abortControllerRef.current === controller) {
              abortControllerRef.current = null;
            }
            return;
          }
          if (abortControllerRef.current !== controller) {
            return;
          }
          abortControllerRef.current = null;
          const reason = classifyError(err);
          logger.error("image-generate request failed", {
            error: err instanceof Error ? err.message.slice(0, 200) : String(err)
          });
          setGenerateState({
            kind: "error",
            reason
          });
          onEvent("image_generation_failed", {
            mode,
            reason
          });
          const toastKey = reason === "rate_limit" ? "rateLimit" : reason === "network" ? "network" : "failed";
          showToast({
            id: "image-gen-failed",
            message: t(`error.${toastKey}`),
            variant: "error",
            action: {
              label: t("cta.tryAgain"),
              onClick: () => {
                runGeneration(true);
              }
            }
          });
        }
      });
    };
    t9 = function handleGenerateClick() {
      runGeneration(false);
    };
    $[12] = generateMutation;
    $[13] = hasUserPrompt;
    $[14] = marketTitle;
    $[15] = mode;
    $[16] = outcomeLabel;
    $[17] = t;
    $[18] = trimmedPrompt;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  const handleGenerateClick = t9;
  let t10;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = function handleTryAgain() {
      setGenerateState({
        kind: "idle"
      });
      setUserPrompt("");
      setCropRegion(INITIAL_CROP_DEFAULT);
      setCompletedCrop(undefined);
    };
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const handleTryAgain = t10;
  let t11;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = function handleCancelGeneration() {
      abortInFlight();
      setGenerateState({
        kind: "idle"
      });
    };
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  const handleCancelGeneration = t11;
  let t12;
  if ($[22] !== mode) {
    t12 = mode === "cover" ? {
      w: 1024,
      h: 1024
    } : {
      w: 512,
      h: 512
    };
    $[22] = mode;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  const t13 = mode === "cover" ? "cover" : "outcome";
  let t14;
  if ($[24] !== t12 || $[25] !== t13) {
    t14 = {
      aspect: 1,
      circular: false,
      outputSize: t12,
      fileNameStem: t13
    };
    $[24] = t12;
    $[25] = t13;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  const cropConfig = t14;
  let t15;
  if ($[27] !== cropConfig) {
    t15 = function fitCropToImage(img) {
      const width = img.clientWidth || img.width;
      const height = img.clientHeight || img.height;
      const imgAspect = width / height;
      const cropAspect = cropConfig.aspect;
      let cropW;
      let cropH;
      if (cropAspect >= imgAspect) {
        cropW = width * 0.9;
        cropH = cropW / cropAspect;
      } else {
        cropH = height * 0.9;
        cropW = cropH * cropAspect;
      }
      return {
        unit: "px",
        x: (width - cropW) / 2,
        y: (height - cropH) / 2,
        width: cropW,
        height: cropH
      };
    };
    $[27] = cropConfig;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  const fitCropToImage = t15;
  let t16;
  if ($[29] !== fitCropToImage) {
    t16 = function handleCropImageLoad(e) {
      const img_0 = e.currentTarget;
      cropImgRef.current = img_0;
      const pxCrop = fitCropToImage(img_0);
      setCropRegion(pxCrop);
      setCompletedCrop(pxCrop);
    };
    $[29] = fitCropToImage;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  const handleCropImageLoad = t16;
  let t17;
  if ($[31] !== fitCropToImage) {
    t17 = function handleSearchCropImageLoad(e_0) {
      const img_1 = e_0.currentTarget;
      searchCropImgRef.current = img_1;
      const pxCrop_0 = fitCropToImage(img_1);
      setSearchCropRegion(pxCrop_0);
      setSearchCompletedCrop(pxCrop_0);
    };
    $[31] = fitCropToImage;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  const handleSearchCropImageLoad = t17;
  let t18;
  if ($[33] !== fitCropToImage) {
    t18 = function handleUploadCropImageLoad(e_1) {
      const img_2 = e_1.currentTarget;
      uploadCropImgRef.current = img_2;
      const pxCrop_1 = fitCropToImage(img_2);
      setUploadCropRegion(pxCrop_1);
      setUploadCompletedCrop(pxCrop_1);
    };
    $[33] = fitCropToImage;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  const handleUploadCropImageLoad = t18;
  let t19;
  if ($[35] !== cropConfig || $[36] !== onPending || $[37] !== onReject || $[38] !== t) {
    t19 = async function runDeferredSelection(img_3, crop, analytics, resetConfirming) {
      let file;
      let persistedBytes;
      ;
      try {
        ({
          file,
          persistedBytes
        } = await getCroppedDraftImage(img_3, crop, cropConfig.outputSize, cropConfig.fileNameStem));
      } catch (t20) {
        const err_0 = t20;
        const message = err_0 instanceof Error ? err_0.message : String(err_0);
        logger.error("image-confirm crop failed", {
          error: message.slice(0, 200)
        });
        showToast({
          id: TOAST_IDS.IMAGE_UPLOAD,
          message: t("toast.uploadFailed"),
          variant: "error"
        });
        resetConfirming();
        onReject(err_0 instanceof Error ? err_0 : new Error(message));
        return;
      }
      const previewUrl = URL.createObjectURL(file);
      analytics();
      onPending({
        croppedFile: file,
        previewUrl,
        persistedBytes: persistedBytes ?? undefined
      });
      resetConfirming();
    };
    $[35] = cropConfig;
    $[36] = onPending;
    $[37] = onReject;
    $[38] = t;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  const runDeferredSelection = t19;
  let t20;
  if ($[40] !== completedCrop || $[41] !== mode || $[42] !== runDeferredSelection) {
    t20 = function handleConfirm() {
      if (completedCrop === undefined || cropImgRef.current === null) {
        return;
      }
      setIsConfirming(true);
      runDeferredSelection(cropImgRef.current, completedCrop, () => {
        onEvent("image_generation_used", {
          mode
        });
      }, () => {
        setIsConfirming(false);
      });
    };
    $[40] = completedCrop;
    $[41] = mode;
    $[42] = runDeferredSelection;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  const handleConfirm = t20;
  let t21;
  if ($[44] !== mode || $[45] !== runDeferredSelection || $[46] !== searchCompletedCrop || $[47] !== searchState.kind || $[48] !== searchState.selected) {
    t21 = function handleSearchConfirm() {
      if (searchState.kind !== "cropping" || searchCompletedCrop === undefined || searchCropImgRef.current === null) {
        return;
      }
      const sourceDomain = searchState.selected.sourceDomain;
      setIsSearchConfirming(true);
      runDeferredSelection(searchCropImgRef.current, searchCompletedCrop, () => {
        onEvent("image_search_used", {
          mode,
          sourceDomain
        });
      }, () => {
        setIsSearchConfirming(false);
      });
    };
    $[44] = mode;
    $[45] = runDeferredSelection;
    $[46] = searchCompletedCrop;
    $[47] = searchState.kind;
    $[48] = searchState.selected;
    $[49] = t21;
  } else {
    t21 = $[49];
  }
  const handleSearchConfirm = t21;
  let t22;
  if ($[50] !== runDeferredSelection || $[51] !== uploadCompletedCrop || $[52] !== uploadState.kind) {
    t22 = function handleUploadConfirm() {
      if (uploadState.kind !== "cropping" || uploadCompletedCrop === undefined || uploadCropImgRef.current === null) {
        return;
      }
      setIsUploadConfirming(true);
      runDeferredSelection(uploadCropImgRef.current, uploadCompletedCrop, _temp, () => {
        setIsUploadConfirming(false);
      });
    };
    $[50] = runDeferredSelection;
    $[51] = uploadCompletedCrop;
    $[52] = uploadState.kind;
    $[53] = t22;
  } else {
    t22 = $[53];
  }
  const handleUploadConfirm = t22;
  let t23;
  if ($[54] !== tImageUpload || $[55] !== uploadState.kind || $[56] !== uploadState.src) {
    t23 = function handleUploadFileSelected(file_0) {
      const error = validateImageUpload(file_0);
      if (error !== null) {
        const message_0 = error === "invalid_type" ? tImageUpload("error_invalid_type") : tImageUpload("error_too_large", {
          maxMB: IMAGE_UPLOAD_MAX_MB
        });
        showToast({
          id: TOAST_IDS.IMAGE_UPLOAD_ERROR,
          message: message_0,
          variant: "error"
        });
        return;
      }
      if (uploadState.kind === "cropping") {
        URL.revokeObjectURL(uploadState.src);
      }
      const src = URL.createObjectURL(file_0);
      setUploadCompletedCrop(undefined);
      setUploadCropRegion(INITIAL_CROP_DEFAULT);
      setUploadState({
        kind: "cropping",
        src,
        file: file_0
      });
    };
    $[54] = tImageUpload;
    $[55] = uploadState.kind;
    $[56] = uploadState.src;
    $[57] = t23;
  } else {
    t23 = $[57];
  }
  const handleUploadFileSelected = t23;
  let t24;
  if ($[58] !== uploadState.kind || $[59] !== uploadState.src) {
    t24 = function handleUploadCancel() {
      if (uploadState.kind === "cropping") {
        URL.revokeObjectURL(uploadState.src);
      }
      setUploadState({
        kind: "idle"
      });
      setUploadCompletedCrop(undefined);
      setUploadCropRegion(INITIAL_CROP_DEFAULT);
    };
    $[58] = uploadState.kind;
    $[59] = uploadState.src;
    $[60] = t24;
  } else {
    t24 = $[60];
  }
  const handleUploadCancel = t24;
  let t25;
  if ($[61] !== mode || $[62] !== searchInput || $[63] !== searchQuery || $[64] !== searchState.kind) {
    t25 = function handleSearchSubmit(e_2) {
      e_2.preventDefault();
      const trimmed = searchInput.trim();
      if (trimmed.length === 0 || trimmed.length > 200) {
        return;
      }
      if (searchState.kind !== "results") {
        setSearchState({
          kind: "results"
        });
      }
      setSearchCompletedCrop(undefined);
      searchStartTimeRef.current = Date.now();
      lastSeenPageCountRef.current = 0;
      if (searchQuery !== trimmed) {
        setSearchQuery(trimmed);
      }
      onEvent("image_search_started", {
        mode,
        queryLength: trimmed.length,
        autoTriggered: false
      });
    };
    $[61] = mode;
    $[62] = searchInput;
    $[63] = searchQuery;
    $[64] = searchState.kind;
    $[65] = t25;
  } else {
    t25 = $[65];
  }
  const handleSearchSubmit = t25;
  let t26;
  if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = function handleSearchThumbClick(result) {
      setSearchCompletedCrop(undefined);
      setSearchCropRegion(INITIAL_CROP_DEFAULT);
      setSearchState({
        kind: "cropping",
        selected: result
      });
    };
    $[66] = t26;
  } else {
    t26 = $[66];
  }
  const handleSearchThumbClick = t26;
  const handleConfirmRef = useRef(handleConfirm);
  const handleSearchConfirmRef = useRef(handleSearchConfirm);
  const handleUploadConfirmRef = useRef(handleUploadConfirm);
  const handleUploadCancelRef = useRef(handleUploadCancel);
  let t27;
  if ($[67] !== handleConfirm || $[68] !== handleSearchConfirm || $[69] !== handleUploadCancel || $[70] !== handleUploadConfirm) {
    t27 = () => {
      handleConfirmRef.current = handleConfirm;
      handleSearchConfirmRef.current = handleSearchConfirm;
      handleUploadConfirmRef.current = handleUploadConfirm;
      handleUploadCancelRef.current = handleUploadCancel;
    };
    $[67] = handleConfirm;
    $[68] = handleSearchConfirm;
    $[69] = handleUploadCancel;
    $[70] = handleUploadConfirm;
    $[71] = t27;
  } else {
    t27 = $[71];
  }
  useLayoutEffect(t27);
  let t28;
  let t29;
  if ($[72] !== backInterceptorRef || $[73] !== searchState.kind || $[74] !== uploadState.kind) {
    t28 = () => {
      if (backInterceptorRef === undefined) {
        return;
      }
      if (searchState.kind === "cropping") {
        backInterceptorRef.current = () => {
          setSearchState({
            kind: "results"
          });
          setSearchCompletedCrop(undefined);
        };
      } else {
        if (uploadState.kind === "cropping") {
          backInterceptorRef.current = () => {
            handleUploadCancelRef.current();
          };
        } else {
          backInterceptorRef.current = null;
        }
      }
      return () => {
        backInterceptorRef.current = null;
      };
    };
    t29 = [searchState.kind, uploadState.kind, backInterceptorRef];
    $[72] = backInterceptorRef;
    $[73] = searchState.kind;
    $[74] = uploadState.kind;
    $[75] = t28;
    $[76] = t29;
  } else {
    t28 = $[75];
    t29 = $[76];
  }
  useEffect(t28, t29);
  let t30;
  let t31;
  if ($[77] !== completedCrop || $[78] !== generateState.kind || $[79] !== isConfirming || $[80] !== isSearchConfirming || $[81] !== isUploadConfirming || $[82] !== onConfirmStateChange || $[83] !== searchCompletedCrop || $[84] !== searchState.kind || $[85] !== uploadCompletedCrop || $[86] !== uploadState.kind) {
    t30 = () => {
      if (onConfirmStateChange === undefined) {
        return;
      }
      if (searchState.kind === "cropping") {
        onConfirmStateChange({
          confirm: async () => {
            handleSearchConfirmRef.current();
          },
          canConfirm: searchCompletedCrop !== undefined && !isSearchConfirming,
          confirming: isSearchConfirming
        });
      } else {
        if (uploadState.kind === "cropping") {
          onConfirmStateChange({
            confirm: async () => {
              handleUploadConfirmRef.current();
            },
            canConfirm: uploadCompletedCrop !== undefined && !isUploadConfirming,
            confirming: isUploadConfirming
          });
        } else {
          if (generateState.kind === "result") {
            onConfirmStateChange({
              confirm: async () => {
                handleConfirmRef.current();
              },
              canConfirm: completedCrop !== undefined && !isConfirming,
              confirming: isConfirming
            });
          } else {
            onConfirmStateChange(null);
          }
        }
      }
      return () => {
        onConfirmStateChange(null);
      };
    };
    t31 = [searchState.kind, uploadState.kind, generateState.kind, searchCompletedCrop, uploadCompletedCrop, completedCrop, isSearchConfirming, isUploadConfirming, isConfirming, onConfirmStateChange];
    $[77] = completedCrop;
    $[78] = generateState.kind;
    $[79] = isConfirming;
    $[80] = isSearchConfirming;
    $[81] = isUploadConfirming;
    $[82] = onConfirmStateChange;
    $[83] = searchCompletedCrop;
    $[84] = searchState.kind;
    $[85] = uploadCompletedCrop;
    $[86] = uploadState.kind;
    $[87] = t30;
    $[88] = t31;
  } else {
    t30 = $[87];
    t31 = $[88];
  }
  useEffect(t30, t31);
  let t32;
  if ($[89] !== activeTab || $[90] !== mode || $[91] !== searchInput) {
    t32 = () => {
      if (activeTab !== "search") {
        return;
      }
      if (hasAutoFiredRef.current) {
        return;
      }
      const trimmed_0 = searchInput.trim();
      if (trimmed_0.length === 0) {
        return;
      }
      hasAutoFiredRef.current = true;
      searchStartTimeRef.current = Date.now();
      lastSeenPageCountRef.current = 0;
      setSearchQuery(trimmed_0);
      setSearchState({
        kind: "results"
      });
      onEvent("image_search_started", {
        mode,
        queryLength: trimmed_0.length,
        autoTriggered: true
      });
    };
    $[89] = activeTab;
    $[90] = mode;
    $[91] = searchInput;
    $[92] = t32;
  } else {
    t32 = $[92];
  }
  let t33;
  if ($[93] !== activeTab) {
    t33 = [activeTab];
    $[93] = activeTab;
    $[94] = t33;
  } else {
    t33 = $[94];
  }
  useEffect(t32, t33);
  let pickerStep = 1;
  if (activeTab === "search" && searchState.kind === "cropping") {
    pickerStep = 2;
  } else {
    if (activeTab === "generate" && generateState.kind === "result") {
      pickerStep = 2;
    } else {
      if (activeTab === "upload" && uploadState.kind === "cropping") {
        pickerStep = 2;
      }
    }
  }
  let t34;
  if ($[95] !== activeTab || $[96] !== completedCrop || $[97] !== cropConfig || $[98] !== cropRegion || $[99] !== generateState || $[100] !== handleConfirm || $[101] !== handleCropImageLoad || $[102] !== handleGenerateClick || $[103] !== handleSearchConfirm || $[104] !== handleSearchCropImageLoad || $[105] !== handleSearchSubmit || $[106] !== handleUploadCancel || $[107] !== handleUploadConfirm || $[108] !== handleUploadCropImageLoad || $[109] !== handleUploadFileSelected || $[110] !== hasUserPrompt || $[111] !== isConfirming || $[112] !== isSearchConfirming || $[113] !== isUploadConfirming || $[114] !== mode || $[115] !== onCancel || $[116] !== onConfirmStateChange || $[117] !== pickerStep || $[118] !== searchCompletedCrop || $[119] !== searchCropRegion || $[120] !== searchInput || $[121] !== searchQuery || $[122] !== searchState || $[123] !== showCancel || $[124] !== t || $[125] !== tImageUpload || $[126] !== uploadCompletedCrop || $[127] !== uploadCropRegion || $[128] !== uploadState || $[129] !== userPrompt) {
    t34 = {
      mode,
      onCancel,
      showCancel,
      onConfirmStateChange,
      t,
      tImageUpload,
      activeTab,
      setActiveTab,
      userPrompt,
      setUserPrompt,
      generateState,
      cropRegion,
      setCropRegion,
      completedCrop,
      setCompletedCrop,
      isConfirming,
      hasUserPrompt,
      cropConfig,
      searchInput,
      setSearchInput,
      searchQuery,
      searchState,
      setSearchState,
      searchCropRegion,
      setSearchCropRegion,
      searchCompletedCrop,
      setSearchCompletedCrop,
      isSearchConfirming,
      searchStartTimeRef,
      lastSeenPageCountRef,
      uploadState,
      uploadCropRegion,
      setUploadCropRegion,
      uploadCompletedCrop,
      setUploadCompletedCrop,
      isUploadConfirming,
      handleGenerateClick,
      handleTryAgain,
      handleCancelGeneration,
      handleConfirm,
      handleCropImageLoad,
      handleSearchSubmit,
      handleSearchCropImageLoad,
      handleSearchConfirm,
      handleSearchThumbClick,
      handleUploadFileSelected,
      handleUploadCropImageLoad,
      handleUploadConfirm,
      handleUploadCancel,
      pickerStep
    };
    $[95] = activeTab;
    $[96] = completedCrop;
    $[97] = cropConfig;
    $[98] = cropRegion;
    $[99] = generateState;
    $[100] = handleConfirm;
    $[101] = handleCropImageLoad;
    $[102] = handleGenerateClick;
    $[103] = handleSearchConfirm;
    $[104] = handleSearchCropImageLoad;
    $[105] = handleSearchSubmit;
    $[106] = handleUploadCancel;
    $[107] = handleUploadConfirm;
    $[108] = handleUploadCropImageLoad;
    $[109] = handleUploadFileSelected;
    $[110] = hasUserPrompt;
    $[111] = isConfirming;
    $[112] = isSearchConfirming;
    $[113] = isUploadConfirming;
    $[114] = mode;
    $[115] = onCancel;
    $[116] = onConfirmStateChange;
    $[117] = pickerStep;
    $[118] = searchCompletedCrop;
    $[119] = searchCropRegion;
    $[120] = searchInput;
    $[121] = searchQuery;
    $[122] = searchState;
    $[123] = showCancel;
    $[124] = t;
    $[125] = tImageUpload;
    $[126] = uploadCompletedCrop;
    $[127] = uploadCropRegion;
    $[128] = uploadState;
    $[129] = userPrompt;
    $[130] = t34;
  } else {
    t34 = $[130];
  }
  return t34;
}
function _temp() {}
interface MarketPickerController {
  mode: ImageGenerationMode;
  onCancel: (() => void) | undefined;
  showCancel: boolean;
  onConfirmStateChange: MarketImagePickerProps['onConfirmStateChange'];
  t: ReturnType<typeof useTranslations>;
  tImageUpload: ReturnType<typeof useTranslations>;
  activeTab: TabValue;
  setActiveTab: Dispatch<SetStateAction<TabValue>>;
  userPrompt: string;
  setUserPrompt: Dispatch<SetStateAction<string>>;
  generateState: GenerateState;
  cropRegion: Crop;
  setCropRegion: Dispatch<SetStateAction<Crop>>;
  completedCrop: PixelCrop | undefined;
  setCompletedCrop: Dispatch<SetStateAction<PixelCrop | undefined>>;
  isConfirming: boolean;
  hasUserPrompt: boolean;
  cropConfig: {
    aspect: number;
    circular: boolean;
    outputSize: {
      w: number;
      h: number;
    };
    fileNameStem: string;
  };
  searchInput: string;
  setSearchInput: Dispatch<SetStateAction<string>>;
  searchQuery: string;
  searchState: SearchState;
  setSearchState: Dispatch<SetStateAction<SearchState>>;
  searchCropRegion: Crop;
  setSearchCropRegion: Dispatch<SetStateAction<Crop>>;
  searchCompletedCrop: PixelCrop | undefined;
  setSearchCompletedCrop: Dispatch<SetStateAction<PixelCrop | undefined>>;
  isSearchConfirming: boolean;
  searchStartTimeRef: {
    current: number;
  };
  lastSeenPageCountRef: {
    current: number;
  };
  uploadState: UploadState;
  uploadCropRegion: Crop;
  setUploadCropRegion: Dispatch<SetStateAction<Crop>>;
  uploadCompletedCrop: PixelCrop | undefined;
  setUploadCompletedCrop: Dispatch<SetStateAction<PixelCrop | undefined>>;
  isUploadConfirming: boolean;
  handleGenerateClick: () => void;
  handleTryAgain: () => void;
  handleCancelGeneration: () => void;
  handleConfirm: () => void;
  handleCropImageLoad: (e: React.SyntheticEvent<HTMLImageElement>) => void;
  handleSearchSubmit: (e: FormEvent) => void;
  handleSearchCropImageLoad: (e: React.SyntheticEvent<HTMLImageElement>) => void;
  handleSearchConfirm: () => void;
  handleSearchThumbClick: (result: ImageSearchResult) => void;
  handleUploadFileSelected: (file: File) => void;
  handleUploadCropImageLoad: (e: React.SyntheticEvent<HTMLImageElement>) => void;
  handleUploadConfirm: () => void;
  handleUploadCancel: () => void;
  pickerStep: 1 | 2;
}

/**
 * Shared body for the Search / Generate / Upload image picker. Used both
 * inline (cover step inside the create-proposal wizard, where the surrounding
 * wizard already provides a dialog) and inside `<MarketImagePickerModal>`
 * for the outcome thumbs (which need their own dialog because the outcome
 * rows are too tight to host the tabs inline).
 *
 * MUST NOT render any dialog chrome — the parent decides whether to wrap.
 *
 * Search and Upload are always available; the AI Generate tab requires the
 * `ai_image_generation` gate. Search is the leftmost and default-active tab.
 */
export function MarketImagePicker(props) {
  const $ = _c(33);
  const {
    showStepIndicator: t0
  } = props;
  const showStepIndicator = t0 === undefined ? true : t0;
  const c = useMarketImagePicker(props);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = checkGate("ai_image_generation");
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const aiGenerationEnabled = t1;
  let t2;
  if ($[1] !== c) {
    t2 = c.t("tab.search");
    $[1] = c;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== c) {
    t3 = <SearchTabView c={c} />;
    $[3] = c;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t2 || $[6] !== t3) {
    t4 = {
      value: "search",
      label: t2,
      icon: Search01Icon,
      content: t3
    };
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== c) {
    t5 = aiGenerationEnabled ? [{
      value: "generate",
      label: c.t("tab.generate"),
      icon: SparklesIcon,
      content: <GenerateTabView c={c} />
    }] : [];
    $[8] = c;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== c) {
    t6 = c.t("tab.upload");
    $[10] = c;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== c) {
    t7 = <UploadTabView c={c} />;
    $[12] = c;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t6 || $[15] !== t7) {
    t8 = {
      value: "upload",
      label: t6,
      icon: Upload01Icon,
      content: t7
    };
    $[14] = t6;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t4 || $[18] !== t5 || $[19] !== t8) {
    t9 = [t4, ...t5, t8];
    $[17] = t4;
    $[18] = t5;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  const tabsItems = t9;
  let t10;
  if ($[21] !== c.pickerStep || $[22] !== showStepIndicator) {
    t10 = showStepIndicator ? <PickerStepIndicator currentStep={c.pickerStep} /> : null;
    $[21] = c.pickerStep;
    $[22] = showStepIndicator;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== c) {
    t11 = v => {
      if (v === "search" || v === "generate" || v === "upload") {
        c.setActiveTab(v);
      }
    };
    $[24] = c;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== c.activeTab || $[27] !== t11 || $[28] !== tabsItems) {
    t12 = <AppTabs value={c.activeTab} onValueChange={t11} items={tabsItems} listClassName="flex w-full" />;
    $[26] = c.activeTab;
    $[27] = t11;
    $[28] = tabsItems;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== t10 || $[31] !== t12) {
    t13 = <div className="flex flex-col gap-6">{t10}{t12}</div>;
    $[30] = t10;
    $[31] = t12;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  return t13;
}
function GenerateTabView(t0) {
  const $ = _c(46);
  const {
    c
  } = t0;
  const {
    generateState,
    cropRegion,
    cropConfig,
    t,
    mode,
    userPrompt
  } = c;
  let t1;
  if ($[0] !== generateState.kind) {
    t1 = generateState.kind === "generating" && <div className="relative mx-auto aspect-square w-full max-w-sm overflow-hidden rounded-card border border-border-primary bg-background-tertiary" data-testid="image-gen-aspect-frame"><ImageShimmer /></div>;
    $[0] = generateState.kind;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== c || $[3] !== cropConfig || $[4] !== cropRegion || $[5] !== generateState.imageUrl || $[6] !== generateState.kind || $[7] !== t) {
    t2 = generateState.kind === "result" && <div className="mx-auto w-full max-w-sm aspect-square overflow-hidden rounded-card border border-border-primary bg-background-tertiary"><ReactCrop crop={cropRegion} onChange={crop => {
        c.setCropRegion(crop);
      }} onComplete={crop_0 => {
        c.setCompletedCrop(crop_0);
      }} aspect={cropConfig.aspect} circularCrop={false} className="size-full"><img src={generateState.imageUrl} alt={t("state.generating")} onLoad={c.handleCropImageLoad} crossOrigin="anonymous" className="size-full object-cover" data-testid="image-gen-result" /></ReactCrop></div>;
    $[2] = c;
    $[3] = cropConfig;
    $[4] = cropRegion;
    $[5] = generateState.imageUrl;
    $[6] = generateState.kind;
    $[7] = t;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== generateState.kind) {
    t3 = generateState.kind === "error" && <div className="relative mx-auto aspect-square w-full max-w-sm overflow-hidden rounded-card border border-border-primary bg-background-tertiary" data-testid="image-gen-aspect-frame"><div className="absolute inset-0 flex items-center justify-center"><Icon icon={SparklesIcon} className="size-10 text-foreground-tertiary" aria-hidden="true" /></div></div>;
    $[9] = generateState.kind;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== c || $[12] !== generateState.kind || $[13] !== mode || $[14] !== t || $[15] !== userPrompt) {
    t4 = generateState.kind !== "result" && <InputField label={t("promptLabel")} maxLength={500} placeholder={mode === "cover" ? t("placeholder.cover") : t("placeholder.outcome")} value={userPrompt} onChange={e => {
      c.setUserPrompt(e.target.value);
    }} disabled={generateState.kind === "generating"} />;
    $[11] = c;
    $[12] = generateState.kind;
    $[13] = mode;
    $[14] = t;
    $[15] = userPrompt;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  let t5;
  if ($[17] !== generateState.kind || $[18] !== generateState.reason || $[19] !== t) {
    t5 = generateState.kind === "error" && <GenerateErrorAlert reason={generateState.reason} t={t} />;
    $[17] = generateState.kind;
    $[18] = generateState.reason;
    $[19] = t;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  const t6 = c.hasUserPrompt;
  const t7 = c.isConfirming;
  const t8 = c.completedCrop;
  let t9;
  if ($[21] !== c) {
    t9 = c.tImageUpload("cancel");
    $[21] = c;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  const t10 = c.onConfirmStateChange !== undefined;
  let t11;
  if ($[23] !== c) {
    t11 = () => {
      c.handleConfirm();
    };
    $[23] = c;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== c.completedCrop || $[26] !== c.handleCancelGeneration || $[27] !== c.handleGenerateClick || $[28] !== c.handleTryAgain || $[29] !== c.hasUserPrompt || $[30] !== c.isConfirming || $[31] !== c.onCancel || $[32] !== c.showCancel || $[33] !== generateState || $[34] !== t || $[35] !== t10 || $[36] !== t11 || $[37] !== t9) {
    t12 = <GenerateActions state={generateState} hasUserPrompt={t6} isConfirming={t7} completedCrop={t8} cancelLabel={t9} showCancel={c.showCancel} hideInlineConfirm={t10} onGenerate={c.handleGenerateClick} onCancelGeneration={c.handleCancelGeneration} onTryAgain={c.handleTryAgain} onConfirm={t11} onClose={c.onCancel} t={t} />;
    $[25] = c.completedCrop;
    $[26] = c.handleCancelGeneration;
    $[27] = c.handleGenerateClick;
    $[28] = c.handleTryAgain;
    $[29] = c.hasUserPrompt;
    $[30] = c.isConfirming;
    $[31] = c.onCancel;
    $[32] = c.showCancel;
    $[33] = generateState;
    $[34] = t;
    $[35] = t10;
    $[36] = t11;
    $[37] = t9;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  let t13;
  if ($[39] !== t1 || $[40] !== t12 || $[41] !== t2 || $[42] !== t3 || $[43] !== t4 || $[44] !== t5) {
    t13 = <div className="flex flex-col gap-4">{t1}{t2}{t3}{t4}{t5}{t12}</div>;
    $[39] = t1;
    $[40] = t12;
    $[41] = t2;
    $[42] = t3;
    $[43] = t4;
    $[44] = t5;
    $[45] = t13;
  } else {
    t13 = $[45];
  }
  return t13;
}
function UploadTabView(t0) {
  const $ = _c(24);
  const {
    c
  } = t0;
  const {
    uploadState,
    uploadCropRegion,
    cropConfig,
    tImageUpload,
    t
  } = c;
  if (uploadState.kind === "cropping") {
    let t1;
    let t2;
    if ($[0] !== c) {
      t1 = crop => {
        c.setUploadCropRegion(crop);
      };
      t2 = crop_0 => {
        c.setUploadCompletedCrop(crop_0);
      };
      $[0] = c;
      $[1] = t1;
      $[2] = t2;
    } else {
      t1 = $[1];
      t2 = $[2];
    }
    const t3 = cropConfig.aspect;
    const t4 = uploadState.src;
    let t5;
    if ($[3] !== tImageUpload) {
      t5 = tImageUpload("preview_alt");
      $[3] = tImageUpload;
      $[4] = t5;
    } else {
      t5 = $[4];
    }
    let t6;
    if ($[5] !== c.handleUploadCropImageLoad || $[6] !== t5 || $[7] !== uploadState.src) {
      t6 = <img src={t4} alt={t5} onLoad={c.handleUploadCropImageLoad} className="block h-auto max-h-[60vh] w-full object-contain" data-testid="image-upload-crop-image" />;
      $[5] = c.handleUploadCropImageLoad;
      $[6] = t5;
      $[7] = uploadState.src;
      $[8] = t6;
    } else {
      t6 = $[8];
    }
    let t7;
    if ($[9] !== cropConfig.aspect || $[10] !== t1 || $[11] !== t2 || $[12] !== t6 || $[13] !== uploadCropRegion) {
      t7 = <div className="mx-auto flex w-full max-w-sm items-center justify-center"><ReactCrop crop={uploadCropRegion} onChange={t1} onComplete={t2} aspect={t3} circularCrop={false}>{t6}</ReactCrop></div>;
      $[9] = cropConfig.aspect;
      $[10] = t1;
      $[11] = t2;
      $[12] = t6;
      $[13] = uploadCropRegion;
      $[14] = t7;
    } else {
      t7 = $[14];
    }
    let t8;
    if ($[15] !== c || $[16] !== t || $[17] !== tImageUpload) {
      t8 = c.onConfirmStateChange === undefined ? <div className="flex gap-2"><AnimatedButton variant="secondary" onClick={c.handleUploadCancel} data-testid="image-upload-cancel">{tImageUpload("cancel")}</AnimatedButton><AnimatedButton className="flex-1" onClick={() => {
          c.handleUploadConfirm();
        }} disabled={c.isUploadConfirming || c.uploadCompletedCrop === undefined} loading={c.isUploadConfirming} data-testid="image-upload-confirm">{t("cta.confirm")}</AnimatedButton></div> : null;
      $[15] = c;
      $[16] = t;
      $[17] = tImageUpload;
      $[18] = t8;
    } else {
      t8 = $[18];
    }
    let t9;
    if ($[19] !== t7 || $[20] !== t8) {
      t9 = <div className="flex flex-col gap-3" data-testid="image-upload-cropping">{t7}{t8}</div>;
      $[19] = t7;
      $[20] = t8;
      $[21] = t9;
    } else {
      t9 = $[21];
    }
    return t9;
  }
  let t1;
  if ($[22] !== c.handleUploadFileSelected) {
    t1 = <ImageUploadDropzone onFileSelected={c.handleUploadFileSelected} aspectRatio="square" />;
    $[22] = c.handleUploadFileSelected;
    $[23] = t1;
  } else {
    t1 = $[23];
  }
  return t1;
}
function SearchTabView(t0) {
  const $ = _c(17);
  const {
    c
  } = t0;
  const {
    searchState,
    t,
    searchInput,
    searchCropRegion,
    cropConfig,
    searchQuery,
    mode
  } = c;
  const isCropping = searchState.kind === "cropping";
  let t1;
  if ($[0] !== c || $[1] !== isCropping || $[2] !== searchInput || $[3] !== t) {
    t1 = !isCropping && <form onSubmit={c.handleSearchSubmit} className="flex gap-2 p-1"><InputField fullWidth={true} placeholder={t("search.placeholder")} value={searchInput} onChange={e => {
        c.setSearchInput(e.target.value);
      }} maxLength={200} aria-label={t("search.placeholder")} data-testid="image-search-input" /><AnimatedButton type="submit" prefix={Search01Icon} className="h-10" data-testid="image-search-submit" disabled={searchInput.trim().length === 0}>{t("search.cta.search")}</AnimatedButton></form>;
    $[0] = c;
    $[1] = isCropping;
    $[2] = searchInput;
    $[3] = t;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== c || $[6] !== cropConfig || $[7] !== mode || $[8] !== searchCropRegion || $[9] !== searchQuery || $[10] !== searchState.kind || $[11] !== searchState.selected || $[12] !== t) {
    t2 = searchState.kind === "cropping" ? <div className="flex flex-col gap-3"><div className="mx-auto flex w-full max-w-sm items-center justify-center"><ReactCrop crop={searchCropRegion} onChange={crop => {
          c.setSearchCropRegion(crop);
        }} onComplete={crop_0 => {
          c.setSearchCompletedCrop(crop_0);
        }} aspect={cropConfig.aspect} circularCrop={false}><img src={getImageProxy({
            url: searchState.selected.fullUrl
          })} alt={t("search.attribution", {
            domain: searchState.selected.sourceDomain
          })} onLoad={c.handleSearchCropImageLoad} onError={e_0 => {
            if (searchState.kind !== "cropping") {
              return;
            }
            const el = e_0.currentTarget;
            const fallback = getImageProxy({
              url: searchState.selected.thumbnailUrl
            });
            if (el.src !== fallback) {
              el.src = fallback;
            }
          }} crossOrigin="anonymous" className="block h-auto max-h-[60vh] w-full object-contain" data-testid="image-search-crop-image" /></ReactCrop></div>{c.onConfirmStateChange === undefined ? <AnimatedButton onClick={() => {
        c.handleSearchConfirm();
      }} disabled={c.isSearchConfirming || c.searchCompletedCrop === undefined} loading={c.isSearchConfirming} data-testid="image-search-confirm">{t("cta.confirm")}</AnimatedButton> : null}</div> : <SearchGrid query={searchQuery} mode={mode} startTimeRef={c.searchStartTimeRef} lastSeenPageCountRef={c.lastSeenPageCountRef} onSelect={c.handleSearchThumbClick} onError={reason => {
      c.setSearchState({
        kind: "error",
        reason
      });
    }} onSuccess={() => {
      if (searchState.kind === "error") {
        c.setSearchState({
          kind: "results"
        });
      }
    }} t={t} />;
    $[5] = c;
    $[6] = cropConfig;
    $[7] = mode;
    $[8] = searchCropRegion;
    $[9] = searchQuery;
    $[10] = searchState.kind;
    $[11] = searchState.selected;
    $[12] = t;
    $[13] = t2;
  } else {
    t2 = $[13];
  }
  let t3;
  if ($[14] !== t1 || $[15] !== t2) {
    t3 = <div className="flex flex-col gap-4">{t1}{t2}</div>;
    $[14] = t1;
    $[15] = t2;
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  return t3;
}
function PickerStepIndicator(t0) {
  const $ = _c(3);
  const {
    currentStep
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [1, 2];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== currentStep) {
    t2 = <div className="flex gap-1" aria-hidden="true">{t1.map(i => <div key={`picker-step-${String(i)}`} className={cn("h-1.5 flex-1 rounded-full transition-colors", i <= currentStep ? "bg-foreground-primary" : "bg-background-tertiary")} />)}</div>;
    $[1] = currentStep;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}

// ---------------------------------------------------------------------------
// Subcomponents
// ---------------------------------------------------------------------------

interface GenerateErrorAlertProps {
  reason: ImageGenerationFailureReason;
  t: ReturnType<typeof useTranslations>;
}
function GenerateErrorAlert(t0) {
  const $ = _c(5);
  const {
    reason,
    t
  } = t0;
  let key;
  if (reason === "rate_limit") {
    key = "rateLimit";
  } else {
    if (reason === "network") {
      key = "network";
    } else {
      key = "failed";
    }
  }
  const t1 = `error.${key}`;
  let t2;
  if ($[0] !== t || $[1] !== t1) {
    t2 = t(t1);
    $[0] = t;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2) {
    t3 = <Alert variant="destructive" data-testid="image-gen-error"><AlertDescription>{t2}</AlertDescription></Alert>;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}
interface GenerateActionsProps {
  state: GenerateState;
  hasUserPrompt: boolean;
  isConfirming: boolean;
  completedCrop: PixelCrop | undefined;
  cancelLabel: string;
  showCancel: boolean;
  /** When true, the inline Confirm button in the result state is hidden — the
   * parent (wizard) drives confirm via `onConfirmStateChange`. */
  hideInlineConfirm: boolean;
  onGenerate: () => void;
  onCancelGeneration: () => void;
  onTryAgain: () => void;
  onConfirm: () => void;
  onClose?: (() => void) | undefined;
  t: ReturnType<typeof useTranslations>;
}
function GenerateActions(t0) {
  const $ = _c(40);
  const {
    state,
    hasUserPrompt,
    isConfirming,
    completedCrop,
    cancelLabel,
    showCancel,
    hideInlineConfirm,
    onGenerate,
    onCancelGeneration,
    onTryAgain,
    onConfirm,
    onClose,
    t
  } = t0;
  if (state.kind === "generating") {
    let t1;
    if ($[0] !== t) {
      t1 = t("state.generating");
      $[0] = t;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== t1) {
      t2 = <AnimatedButton className="flex-1" disabled={true} loading={true}>{t1}</AnimatedButton>;
      $[2] = t1;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    let t3;
    if ($[4] !== cancelLabel || $[5] !== onCancelGeneration) {
      t3 = <AnimatedButton variant="secondary" onClick={onCancelGeneration} data-testid="image-gen-cancel">{cancelLabel}</AnimatedButton>;
      $[4] = cancelLabel;
      $[5] = onCancelGeneration;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    let t4;
    if ($[7] !== t2 || $[8] !== t3) {
      t4 = <div className="flex gap-2">{t2}{t3}</div>;
      $[7] = t2;
      $[8] = t3;
      $[9] = t4;
    } else {
      t4 = $[9];
    }
    return t4;
  }
  if (state.kind === "result") {
    const t1 = hideInlineConfirm ? "primary" : "secondary";
    let t2;
    if ($[10] !== t) {
      t2 = t("cta.tryAgain");
      $[10] = t;
      $[11] = t2;
    } else {
      t2 = $[11];
    }
    let t3;
    if ($[12] !== onTryAgain || $[13] !== t1 || $[14] !== t2) {
      t3 = <AnimatedButton variant={t1} className="flex-1" onClick={onTryAgain} prefix={RepeatIcon} data-testid="image-gen-try-again">{t2}</AnimatedButton>;
      $[12] = onTryAgain;
      $[13] = t1;
      $[14] = t2;
      $[15] = t3;
    } else {
      t3 = $[15];
    }
    let t4;
    if ($[16] !== completedCrop || $[17] !== hideInlineConfirm || $[18] !== isConfirming || $[19] !== onConfirm || $[20] !== t) {
      t4 = hideInlineConfirm ? null : <AnimatedButton className="flex-1" onClick={onConfirm} disabled={isConfirming || completedCrop === undefined} loading={isConfirming} data-testid="image-gen-confirm">{t("cta.confirm")}</AnimatedButton>;
      $[16] = completedCrop;
      $[17] = hideInlineConfirm;
      $[18] = isConfirming;
      $[19] = onConfirm;
      $[20] = t;
      $[21] = t4;
    } else {
      t4 = $[21];
    }
    let t5;
    if ($[22] !== t3 || $[23] !== t4) {
      t5 = <div className="flex gap-2">{t3}{t4}</div>;
      $[22] = t3;
      $[23] = t4;
      $[24] = t5;
    } else {
      t5 = $[24];
    }
    return t5;
  }
  let t1;
  if ($[25] !== hasUserPrompt || $[26] !== t) {
    t1 = hasUserPrompt ? t("cta.generate") : t("cta.fromTitle");
    $[25] = hasUserPrompt;
    $[26] = t;
    $[27] = t1;
  } else {
    t1 = $[27];
  }
  const ctaLabel = t1;
  if (showCancel && onClose !== undefined) {
    let t2;
    if ($[28] !== ctaLabel || $[29] !== onGenerate) {
      t2 = <AnimatedButton className="flex-1" onClick={onGenerate} prefix={SparklesIcon} data-testid="image-gen-submit">{ctaLabel}</AnimatedButton>;
      $[28] = ctaLabel;
      $[29] = onGenerate;
      $[30] = t2;
    } else {
      t2 = $[30];
    }
    let t3;
    if ($[31] !== cancelLabel || $[32] !== onClose) {
      t3 = <AnimatedButton variant="secondary" onClick={onClose}>{cancelLabel}</AnimatedButton>;
      $[31] = cancelLabel;
      $[32] = onClose;
      $[33] = t3;
    } else {
      t3 = $[33];
    }
    let t4;
    if ($[34] !== t2 || $[35] !== t3) {
      t4 = <div className="flex gap-2">{t2}{t3}</div>;
      $[34] = t2;
      $[35] = t3;
      $[36] = t4;
    } else {
      t4 = $[36];
    }
    return t4;
  }
  let t2;
  if ($[37] !== ctaLabel || $[38] !== onGenerate) {
    t2 = <AnimatedButton className="w-full" onClick={onGenerate} prefix={SparklesIcon} data-testid="image-gen-submit">{ctaLabel}</AnimatedButton>;
    $[37] = ctaLabel;
    $[38] = onGenerate;
    $[39] = t2;
  } else {
    t2 = $[39];
  }
  return t2;
}